import React, { FC } from "react";

import { useTranslation } from "react-i18next";

import { DetailPanel, DetailPanels } from "@/components/organisms";
import config from "@/config";
import { ContractDetail, Permissions } from "@/shared/domain";
import { useAuth, useMediaDisplayer } from "@/shared/hooks";

import {
    panelGeneralConditionsSchema,
    panelManagerSchema,
    panelPaymentSchema,
    panelRiskObjectSchema,
    panelSalesRepresentativeSchema,
} from "./panelSchema";

type ContractTabDetailProps = {
    contractDetails: ContractDetail;
};

const AccordionIndex = {
    RISKOBJECT: "riskObject",
    PAYMENT: "payment",
    GENERAL_CONDITIONS: "generalConditions",
    SALES_REPRESENTATIVE: "salesRepresentative",
    MANAGER: "manager",
};

// Order: Vehicle, building, person, leisure
const FirstsIGToOrder = ["3", "2", "1", "4"];

const ContractTabDetail: FC<ContractTabDetailProps> = ({ contractDetails }) => {
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const { displayFullMode } = useMediaDisplayer();

    const handleReferenceClick = (referenceId: string) => {
        const url = `${config.SPAPI_URL}/api/contract/clause/${encodeURIComponent(referenceId)}`;
        displayFullMode({ title: referenceId, type: "pdf", url });
    };

    const sortIGTypes = (a, b) => {
        return a.id < b.id ? 1 : -1;
    };

    const sortIGCategories = (a, b) => {
        // If the category exist in the order, use them.
        const mapA = FirstsIGToOrder.indexOf(a);
        const mapB = FirstsIGToOrder.indexOf(b);

        return mapA > mapB ? 1 : -1;
    };
    return (
        <DetailPanels defaultDetailPanelOpen={AccordionIndex.RISKOBJECT}>
            {(contractDetails?.listRiskObject || [])
                .sortBy("objectType", sortIGTypes)
                .sortBy("categoryType", sortIGCategories)
                .map((riskObject, index) => (
                    <DetailPanel
                        id={AccordionIndex.RISKOBJECT}
                        key={`riskObject-${riskObject.id}`}
                        detailPanelInfoGroup={contractDetails.listRiskObject.length > 1}
                        lastDetailPanelInGroup={
                            contractDetails.listRiskObject.length > 1 &&
                            index === contractDetails.listRiskObject.length - 1
                        }
                        title={index === 0 && t("Common.WarrantiesOverview")}
                        infoList={panelRiskObjectSchema(riskObject, i18n.language, index)}
                    />
                ))}
            {user.hasPermission(Permissions.AccessLastPayment, contractDetails.policyholderId) && (
                <DetailPanel
                    id={AccordionIndex.PAYMENT}
                    title={t("Common.PaymentDetails")}
                    infoList={panelPaymentSchema(contractDetails, i18n.language)}
                />
            )}
            {user.hasPermission(Permissions.ViewGeneralConditions, contractDetails.policyholderId) && (
                <DetailPanel
                    id={AccordionIndex.GENERAL_CONDITIONS}
                    title={t("Common.GeneralConditions")}
                    infoList={panelGeneralConditionsSchema(contractDetails, handleReferenceClick)}
                />
            )}
            {user.hasPermission(Permissions.AccessContractManager, contractDetails.policyholderId) && (
                <DetailPanel
                    id={AccordionIndex.MANAGER}
                    title={t("Common.Manager")}
                    infoList={panelManagerSchema(contractDetails)}
                />
            )}
            {user.hasPermission(Permissions.AccessSalesRep, contractDetails.policyholderId) && (
                <DetailPanel
                    id={AccordionIndex.SALES_REPRESENTATIVE}
                    title={t("Common.SalesRepresentative")}
                    infoList={panelSalesRepresentativeSchema(contractDetails)}
                />
            )}
        </DetailPanels>
    );
};

export default ContractTabDetail;
